import { useQueryClient } from '@tanstack/react-query';

import { useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';

import { useToaster } from '../../../../hooks/useToaster/useToaster.tsx';
import { featuresSelectors } from '../../../../redux/features/selectors/featuresSelectors.ts';
import { useAppSelector } from '../../../../redux/hooks.ts';
import type { OnboardingModuleProps } from '../../types.ts';
import { OnboardingFormStep } from '../OnboardingFormStep/OnboardingFormStep.tsx';
import { SubscriptionStep } from '../SubscriptionStep/SubscriptionStep.tsx';

const { selectShouldAllowTrialWithoutPayment } = featuresSelectors;

export const OnboardingWizard = ({ SubscriptionModule }: OnboardingModuleProps) => {
  const [step, incrementStep] = useReducer((prev: number) => prev + 1, 0);
  const toast = useToaster();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const allowTrialWithoutPayment = useAppSelector((state) =>
    selectShouldAllowTrialWithoutPayment(state, null),
  );

  if (allowTrialWithoutPayment) {
    return (
      <OnboardingFormStep
        onFinish={async () => {
          await queryClient.invalidateQueries({
            queryKey: ['api', 'v2', 'stripe', 'subscriptionStatus'],
          });
          navigate('/orders');
        }}
      />
    );
  }

  if (step === 0) return <OnboardingFormStep onFinish={incrementStep} />;

  if (step === 1) {
    return (
      <SubscriptionStep
        SubscriptionModule={SubscriptionModule}
        onFinish={async () => {
          toast({
            id: 'onboarding-subscription-success',
            type: 'success',
            title: 'Subscription successful!',
            body: 'Thank you for subscribing to Appraiser Toolkit!',
          });

          await queryClient.invalidateQueries({
            queryKey: ['api', 'v2', 'stripe', 'subscriptionStatus'],
          });

          navigate('/orders');
        }}
      />
    );
  }

  throw new Error(`Invalid step: ${step}`);
};
